import React, { useState } from 'react';
import './WorkoutImporter.css';

/**
 * Parse user-friendly workout JSON into internal format
 *
 * User-friendly format:
 * {
 *   "name": "10 Mile Easy + Strides",
 *   "description": "Long run with strides",
 *   "steps": [
 *     { "distance": "10 miles", "pace": "easy", "notes": "Steady effort" },
 *     { "time": "5 minutes", "pace": "threshold" },
 *     { "repeat": 6, "steps": [
 *       { "time": "20 seconds", "pace": "repetition" },
 *       { "time": "1 minute", "pace": "easy" }
 *     ]}
 *   ]
 * }
 */

// Parse distance string like "10 miles", "5km", "1.5 km", "400m"
const parseDistance = (distStr) => {
  if (!distStr) return null;
  const str = distStr.toString().toLowerCase().trim();

  // Match number and unit
  const match = str.match(/^([\d.]+)\s*(miles?|mi|km|kilometers?|m|meters?)$/);
  if (!match) return null;

  const value = parseFloat(match[1]);
  const unit = match[2];

  if (unit.startsWith('mile') || unit === 'mi') {
    return { value, unit: 'miles', km: value * 1.60934 };
  }
  if (unit === 'km' || unit.startsWith('kilometer')) {
    return { value, unit: 'km', km: value };
  }
  if (unit === 'm' || unit.startsWith('meter')) {
    return { value: value / 1000, unit: 'km', km: value / 1000 };
  }
  return null;
};

// Parse time string like "20 seconds", "5 minutes", "1:30", "1 hour"
const parseTime = (timeStr) => {
  if (!timeStr) return null;
  const str = timeStr.toString().toLowerCase().trim();

  // Match mm:ss or hh:mm:ss format
  const colonMatch = str.match(/^(\d+):(\d+)(?::(\d+))?$/);
  if (colonMatch) {
    if (colonMatch[3]) {
      // hh:mm:ss
      return parseInt(colonMatch[1]) * 3600 + parseInt(colonMatch[2]) * 60 + parseInt(colonMatch[3]);
    }
    // mm:ss
    return parseInt(colonMatch[1]) * 60 + parseInt(colonMatch[2]);
  }

  // Match number and unit
  const match = str.match(/^([\d.]+)\s*(seconds?|secs?|s|minutes?|mins?|m|hours?|hrs?|h)$/);
  if (!match) return null;

  const value = parseFloat(match[1]);
  const unit = match[2];

  if (unit.startsWith('second') || unit.startsWith('sec') || unit === 's') {
    return Math.round(value);
  }
  if (unit.startsWith('minute') || unit.startsWith('min') || unit === 'm') {
    return Math.round(value * 60);
  }
  if (unit.startsWith('hour') || unit.startsWith('hr') || unit === 'h') {
    return Math.round(value * 3600);
  }
  return null;
};

// Normalize pace zone name
const normalizePaceZone = (pace) => {
  if (!pace) return null;
  const p = pace.toString().toLowerCase().trim();

  const mapping = {
    'e': 'easy', 'easy': 'easy',
    'm': 'marathon', 'marathon': 'marathon', 'mp': 'marathon',
    't': 'threshold', 'threshold': 'threshold', 'tempo': 'threshold',
    'i': 'interval', 'interval': 'interval',
    'r': 'repetition', 'repetition': 'repetition', 'rep': 'repetition', 'reps': 'repetition',
    'recovery': 'easy', 'jog': 'easy'
  };

  return mapping[p] || null;
};

// Convert user-friendly step to internal format
const convertStep = (step) => {
  // Handle repeat block
  if (step.repeat) {
    return {
      type: 'repeat',
      repeat_count: parseInt(step.repeat) || 2,
      steps: (step.steps || []).map(convertStep)
    };
  }

  // Regular step
  const result = {
    type: 'step',
    step_type: 'run',
    duration_type: 'distance',
    duration_value: 1,
    duration_unit: 'km',
    pace_zone: null,
    notes: step.notes || ''
  };

  // Parse distance
  if (step.distance) {
    const dist = parseDistance(step.distance);
    if (dist) {
      result.duration_type = 'distance';
      result.duration_value = dist.value;
      result.duration_unit = dist.unit;
    }
  }

  // Parse time (overrides distance if both present)
  if (step.time) {
    const seconds = parseTime(step.time);
    if (seconds) {
      result.duration_type = 'time';
      result.duration_value = seconds;
      result.duration_unit = 'seconds';
    }
  }

  // Parse pace zone
  if (step.pace) {
    result.pace_zone = normalizePaceZone(step.pace);
  }

  return result;
};

// Parse the full workout JSON
const parseWorkoutJson = (jsonStr) => {
  try {
    const data = JSON.parse(jsonStr);

    if (!data.name && !data.workout_name) {
      return { error: 'Missing workout name' };
    }

    const steps = (data.steps || []).map(convertStep);

    return {
      success: true,
      workout: {
        workout_name: data.name || data.workout_name,
        description: data.description || '',
        quality: data.quality === true,
        long_run: data.long_run === true,
        steps: steps
      }
    };
  } catch (e) {
    return { error: `Invalid JSON: ${e.message}` };
  }
};

// Format time for display
const formatTime = (seconds) => {
  if (!seconds) return '0:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Get step summary for preview
const getStepSummary = (step) => {
  if (step.type === 'repeat') {
    const innerSteps = step.steps.map(getStepSummary).join(' + ');
    return `${step.repeat_count}x (${innerSteps})`;
  }

  let duration = '';
  if (step.duration_type === 'distance') {
    duration = `${step.duration_value} ${step.duration_unit}`;
  } else {
    duration = formatTime(step.duration_value);
  }

  const pace = step.pace_zone ? ` @ ${step.pace_zone}` : '';
  return `${duration}${pace}`;
};

// Convert duration value for API (km for distance, seconds for time)
const convertDurationValue = (step) => {
  let value = step.duration_value || 0;

  if (step.duration_type === 'time') {
    // Already in seconds
    return value;
  }

  if (step.duration_type === 'distance') {
    // Convert to km
    if (step.duration_unit === 'miles') {
      return value * 1.60934;
    }
    return value; // already in km
  }

  return value;
};

// Convert frontend steps to backend API format
const convertStepsToApiFormat = (items) => {
  const apiSteps = [];
  let stepOrder = 1;

  const processStep = (step) => {
    apiSteps.push({
      step_order: stepOrder++,
      step_type: 'active', // Backend expects: warmup, active, interval, recovery, cooldown, rest
      duration_type: step.duration_type || 'distance',
      duration_value: convertDurationValue(step),
      pace_type: step.pace_zone || null,
      description: step.notes || null
    });
  };

  items.forEach(item => {
    if (item.type === 'repeat') {
      // Expand repeat blocks - each iteration becomes separate steps
      for (let i = 0; i < (item.repeat_count || 1); i++) {
        (item.steps || []).forEach(step => processStep(step));
      }
    } else {
      processStep(item);
    }
  });

  return apiSteps;
};

// Derive workout type from steps' pace zones
// Priority: R > I > T > M > L > E (most intense wins)
const deriveWorkoutType = (items) => {
  const paceZoneToType = {
    'repetition': 'R',
    'interval': 'I',
    'threshold': 'T',
    'marathon': 'M',
    'long': 'L',
    'easy': 'E'
  };

  const typePriority = ['E', 'L', 'M', 'T', 'I', 'R'];
  let highestType = 'E';

  const checkStep = (step) => {
    const paceZone = step.pace_zone;
    if (paceZone && paceZoneToType[paceZone]) {
      const stepType = paceZoneToType[paceZone];
      if (typePriority.indexOf(stepType) > typePriority.indexOf(highestType)) {
        highestType = stepType;
      }
    }
  };

  items.forEach(item => {
    if (item.type === 'repeat') {
      (item.steps || []).forEach(step => checkStep(step));
    } else {
      checkStep(item);
    }
  });

  return highestType;
};

const WorkoutImporter = ({ isOpen, onClose, onImport, selectedDate }) => {
  const [jsonInput, setJsonInput] = useState('');
  const [parsedWorkout, setParsedWorkout] = useState(null);
  const [error, setError] = useState('');
  const [date, setDate] = useState(selectedDate || new Date().toISOString().split('T')[0]);

  if (!isOpen) return null;

  const handleParse = () => {
    setError('');
    setParsedWorkout(null);

    if (!jsonInput.trim()) {
      setError('Please paste workout JSON');
      return;
    }

    const result = parseWorkoutJson(jsonInput);
    if (result.error) {
      setError(result.error);
    } else {
      setParsedWorkout(result.workout);
    }
  };

  const handleImport = () => {
    if (!parsedWorkout) return;

    // Convert steps to API format
    const apiSteps = parsedWorkout.steps.length > 0
      ? convertStepsToApiFormat(parsedWorkout.steps)
      : null;

    // Calculate total distance and duration from converted steps
    let totalDistance = 0;
    let totalDuration = 0;

    if (apiSteps) {
      apiSteps.forEach(step => {
        if (step.duration_type === 'distance' && step.duration_value) {
          totalDistance += step.duration_value; // Already in km
        } else if (step.duration_type === 'time' && step.duration_value) {
          totalDuration += step.duration_value; // In seconds
        }
      });
    }

    // Derive workout type from pace zones
    const workoutType = parsedWorkout.steps.length > 0
      ? deriveWorkoutType(parsedWorkout.steps)
      : 'E';

    // Build workout data in API format
    const workoutData = {
      date: date,
      type: workoutType,
      workout_name: parsedWorkout.workout_name || null,
      description: parsedWorkout.description || null,
      quality: parsedWorkout.quality || false,
      long_run: parsedWorkout.long_run || false,
      distance_km: totalDistance > 0 ? totalDistance : null,
      duration_minutes: totalDuration > 0 ? Math.round(totalDuration / 60) : null,
      steps: apiSteps
    };

    // Debug: log the data being sent
    console.log('Importing workout data:', JSON.stringify(workoutData, null, 2));

    onImport(workoutData);

    // Reset state
    setJsonInput('');
    setParsedWorkout(null);
    setError('');
    onClose();
  };

  const handleClose = () => {
    setJsonInput('');
    setParsedWorkout(null);
    setError('');
    onClose();
  };

  const exampleJson = `{
  "name": "10 Mile Easy + Strides",
  "description": "Long run with speed development",
  "steps": [
    { "distance": "10 miles", "pace": "easy" },
    { "repeat": 6, "steps": [
      { "time": "20 seconds", "pace": "repetition" },
      { "time": "1 minute", "pace": "easy" }
    ]}
  ]
}`;

  return (
    <div className="workout-importer-overlay" onClick={handleClose}>
      <div className="workout-importer" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>&times;</button>

        <div className="importer-header">
          <h2>Import Workout</h2>
          <p>Paste structured workout JSON to import</p>
        </div>

        <div className="importer-body">
          {error && <div className="importer-error">{error}</div>}

          <div className="form-group">
            <label>Workout Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Workout JSON</label>
            <textarea
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder={exampleJson}
              rows={12}
              className="json-input"
            />
          </div>

          <button type="button" className="btn-parse" onClick={handleParse}>
            Parse Workout
          </button>

          {parsedWorkout && (
            <div className="workout-preview">
              <h3>Preview</h3>
              <div className="preview-name">{parsedWorkout.workout_name}</div>
              {parsedWorkout.description && (
                <div className="preview-description">{parsedWorkout.description}</div>
              )}
              <div className="preview-steps">
                <h4>Steps:</h4>
                <ul>
                  {parsedWorkout.steps.map((step, i) => (
                    <li key={i}>{getStepSummary(step)}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="importer-actions">
            <button type="button" className="btn-cancel" onClick={handleClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-import"
              onClick={handleImport}
              disabled={!parsedWorkout}
            >
              Import to Calendar
            </button>
          </div>

          <div className="format-help">
            <h4>Supported Format</h4>
            <pre>{`{
  "name": "Workout Name",
  "description": "Optional notes",
  "steps": [
    { "distance": "5 km", "pace": "easy" },
    { "time": "20 minutes", "pace": "threshold" },
    { "repeat": 6, "steps": [
      { "time": "30 seconds", "pace": "repetition" },
      { "time": "90 seconds", "pace": "easy" }
    ]}
  ]
}`}</pre>
            <div className="format-notes">
              <p><strong>Distance:</strong> "10 miles", "5 km", "400m"</p>
              <p><strong>Time:</strong> "20 seconds", "5 minutes", "1:30", "1 hour"</p>
              <p><strong>Pace:</strong> easy, marathon, threshold, interval, repetition</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkoutImporter;
